import * as SelectPrimitive from '@radix-ui/react-select';
import type { LucideProps } from 'lucide-react';
import { CheckIcon, ChevronDown } from 'lucide-react';
import * as React from 'react';
import { useLocale } from '../../lib/hooks/use-locale.ts';
import { cn } from '../../lib/utils.ts';
import { Button } from './button.tsx';

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue: React.ForwardRefExoticComponent<
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Value> &
        React.RefAttributes<React.ElementRef<typeof SelectPrimitive.Value>>
> = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Value>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Value>
>(({ className, children, ...props }, ref) => (
    <SelectPrimitive.Value ref={ref} className={cn(className)} {...props}>
        {children}
    </SelectPrimitive.Value>
));
SelectValue.displayName = SelectPrimitive.Value.displayName;

const SelectTrigger = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
        loading?: boolean;
        icon?: React.ComponentType<LucideProps>;
        variant?: 'default' | 'ghost';
    }
>(({ loading, icon, className, children, variant, ...props }, ref) => (
    <SelectPrimitive.Trigger
        ref={ref}
        className={cn('relative flex items-center justify-between', className)}
        {...props}
        asChild
    >
        <Button
            className="font-normal"
            variant={variant === 'ghost' ? 'select-ghost' : 'outline'}
            rightIcon={ChevronDown}
            rightIconProps={{
                rotateOnActive: true,
            }}
            leftIcon={icon}
            isLoading={loading}
        >
            {children}
        </Button>
    </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectTriggerNoStyling = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & { loading?: boolean }
>(({ loading, className, children, ...props }, ref) => (
    <SelectPrimitive.Trigger
        ref={ref}
        className={cn(
            'relative flex w-full items-center justify-between disabled:cursor-not-allowed disabled:opacity-50',
            className,
        )}
        {...props}
    >
        {children}
    </SelectPrimitive.Trigger>
));
SelectTriggerNoStyling.displayName = 'SelectTriggerNoStyling';

const SelectContent = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', onEscapeKeyDown, ...props }, ref) => (
    <SelectPrimitive.Portal>
        <SelectPrimitive.Content
            ref={ref}
            className={cn(
                'bg-popover text-popover-foreground animate-in fade-in-80 relative z-50 min-w-32 overflow-hidden rounded-md border shadow-md',
                position === 'popper' && 'translate-y-1',
                className,
            )}
            position={position}
            // Radix and Base UI keep separate dismissal stacks, so an Escape this layer
            // handles still reaches the Base UI Dialog it may be nested inside and closes
            // that too. The innermost open layer owns the key, so stop it here (RETAIN-1180).
            onEscapeKeyDown={(event) => {
                onEscapeKeyDown?.(event);
                event.stopPropagation();
            }}
            {...props}
        >
            <SelectPrimitive.Viewport
                id="viewport"
                className={cn(
                    'p-1',
                    position === 'popper' &&
                        'h-(--radix-select-trigger-height) w-full min-w-(--radix-select-trigger-width)',
                    'max-h-(--radix-select-content-available-height)',
                )}
            >
                {children}
            </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Label>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
    <SelectPrimitive.Label
        ref={ref}
        className={cn('py-1.5 pl-8 pr-2 text-base font-semibold', className)}
        {...props}
    />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Item>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
    <SelectPrimitive.Item
        ref={ref}
        className={cn(
            'focus:bg-accent focus:text-accent-foreground relative flex w-full',
            'cursor-pointer select-none items-center rounded-xs py-2 px-3 gap-3',
            'outline-hidden data-disabled:pointer-events-none data-disabled:opacity-50 text-sm',
            className,
        )}
        {...props}
    >
        <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
        <SelectPrimitive.ItemIndicator>
            <CheckIcon className="h-4 w-4" />
        </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Separator>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
    <SelectPrimitive.Separator ref={ref} className={cn('bg-muted -mx-1 my-1 h-px', className)} {...props} />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

function SelectNoOptions() {
    const { t } = useLocale();
    return (
        <div className="text-muted-foreground p-2 text-base">{t('select_no_options', { ns: 'common' })}</div>
    );
}

export {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectNoOptions,
    SelectSeparator,
    SelectTrigger,
    SelectTriggerNoStyling,
    SelectValue,
};
